import React, { useState, useMemo } from "react";
import { getFacetTheme } from "../assets/facetTheme";
import { formatPct, getCountryName } from "../lib/format";
import "../styling/Facets.css";

const EMPTY_SELECTION = new Set();

export default function FacetList({
	data = [],
	loading = false,
	filters,
	setFilters,
	field,
	showPct = true,
	category
}) {
	const [countAsc, setCountAsc] = useState(false);
	const [query, setQuery] = useState("");

	const theme = getFacetTheme(category);
	const PALETTE = theme.primary;
	const DIM = theme.dim;

	const sel = filters.get(field) || EMPTY_SELECTION;

	const terms = useMemo(
		() => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
		[query]
	);

	const searchExceptions = ["cancerTypes", "metastaticPatterns", "biomarkerMeasurements", "drugNames"];


	const { withCount, withoutCount } = useMemo(() => {
		const with_ = [];
		const without = [];
		data.forEach((item) => {
			if (item.count > 0) with_.push(item);
			else without.push(item);
		});
		return { withCount: with_, withoutCount: without };
	}, [data]);

	const sortedWithCount = useMemo(() => {
		const sorted = [...withCount].sort((a, b) =>
			countAsc ? a.count - b.count : b.count - a.count
		);
		if (sel.size > 0) {
			const selected = sorted.filter((item) => sel.has(item.name));
			const unselected = sorted.filter((item) => !sel.has(item.name));
			return [...selected, ...unselected];
		}
		return sorted;
	}, [withCount, countAsc, sel]);

	const sortedWithoutCount = useMemo(() => {
		return [...withoutCount].sort((a, b) => a.name.localeCompare(b.name));
	}, [withoutCount]);

	const sorted = useMemo(
		() => [...sortedWithCount, ...sortedWithoutCount],
		[sortedWithCount, sortedWithoutCount]
	);

	const visible = useMemo(() => {
		return sorted.filter((d) => {
			if (d.count === 0) return false;
			if (terms.length === 0) return true;
			const name = d.name.toLowerCase();
			return terms.every((t) => name.includes(t));
		});
	}, [sorted, terms]);

	const canSelectAll = terms.length > 0 && visible.some((d) => !sel.has(d.name));

	function handleSelectAll() {
		setFilters((prev) => {
			const next = new Map(prev);
			const curr = new Set(next.get(field) || []);
			visible.forEach((d) => curr.add(d.name));
			next.set(field, curr);
			return next;
		});
	}

	if (loading) {
		return (
			<div className="list-card">
				<div className="facet-loading">
					<div className="spinner"></div>
					<div className="loading-text">Loading data...</div>
				</div>
			</div>
		);
	}

	return (
		<div className="list-card facet-enter">
			<div className="list-toolbar">
				<input
					type="text"
					placeholder={searchExceptions.includes(field) ? "Search values, separate terms with space" : "Search values..."}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					className="list-search"
				/>
				{query && (
					<span className="clear" onClick={() => setQuery("")}>✕</span>
				)}
			</div>

			{canSelectAll && visible.length > 0 && (
				<div className="list-select-bar">
					<button className="select-all-btn" onClick={handleSelectAll}>
						Select all {visible.length} results
					</button>
				</div>
			)}

			<div className="list-content">
				<div className="list-content-header table-grid">
					<div>&nbsp;</div>
					<div
						className="list-content-header-count"
						onClick={() => setCountAsc(!countAsc)}
						title="Sort by count"
					>
						Count {countAsc ? "▲" : "▼"}
					</div>
					{showPct && <div style={{ textAlign: "right" }}>%</div>}
				</div>

				<div className="simple-list" role="list">
					{visible.map((d) => {
						const hasCount = d.count > 0;
						const p = hasCount && d.percentage ? parseFloat(d.percentage) : 0;
						const pctLabel = hasCount ? formatPct(d.percentage) : "0%";

						const isSelected = sel.has(d.name);
						const isDim = !hasCount || (sel.size > 0 && !isSelected);

						return (
							<div
								key={d.name}
								role="listitem"
								className="list-content-row table-grid"
								aria-selected={isSelected}
								data-muted={isDim || undefined}
								title={d.name}
								onClick={() => {
									setFilters((prev) => {
										const next = new Map(prev);
										const curr = new Set(next.get(field) || []);
										if (curr.has(d.name)) curr.delete(d.name);
										else curr.add(d.name);
										next.set(field, curr.size > 0 ? curr : new Set());
										return next;
									});
								}}
								style={{
									"--bar-color": isDim ? DIM : PALETTE,
									"--bar-width": `${p}%`,
									boxShadow: isSelected ? `${PALETTE} 0px 0px 4px` : "none",
									opacity: isDim ? 0.4 : 1,
									backgroundColor: isSelected
										? "rgba(217, 179, 255, 0.1)"
										: "transparent",
								}}
							>
								<div className="list-content-value">
									{field === "countries" ? getCountryName(d.name) : d.name}
								</div>
								<div className="list-content-count">{d.count}</div>
								{showPct && <div className="list-content-pct">{pctLabel}</div>}
							</div>
						);
					})}

					{!visible.length && (
						<div className="list-content-row empty">No matches</div>
					)}
				</div>
			</div>
		</div>
	);
}